import type {
  ActivityCreatePayload,
  ActivityLocationPayload,
} from "../repositories/activitiesRepository";
import type { FormChatState } from "../repositories/formChat.repository";
import type { ActivityType } from "../types/activity.types";
import { isActivityLocationPayload } from "./activityChatLocationFields";

interface BuildActivityPayloadInput {
  data: FormChatState;
  terreiroId?: string;
  mediumId?: string;
  activityType?: ActivityType;
}

const omitFields = (obj: Record<string, unknown>, fields: string[]) => {
  const result: Record<string, unknown> = {};
  Object.keys(obj).forEach((key) => {
    if (!fields.includes(key)) result[key] = obj[key];
  });
  return result;
};

const buildDefaultMinConfirmedMembers = (memberCount: number) =>
  Math.max(1, Math.floor(memberCount / 2) + 1);

const optionalText = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const resolveActivityLocation = (
  data: FormChatState,
): { isInTerreiro: boolean; location: ActivityLocationPayload } | Record<string, never> => {
  const location = isActivityLocationPayload(data.location) ? data.location : undefined;
  const isInTerreiro =
    typeof data.isInTerreiro === "boolean" ? data.isInTerreiro : location?.isInTerreiro;

  if (typeof isInTerreiro !== "boolean") return {};

  if (isInTerreiro) {
    const knownLocation = optionalText(location?.knownLocation);
    return {
      isInTerreiro: true,
      location: knownLocation
        ? { isInTerreiro: true, knownLocation }
        : { isInTerreiro: true },
    };
  }

  if (!location) return { isInTerreiro: false, location: { isInTerreiro: false } };

  return {
    isInTerreiro: false,
    location: {
      isInTerreiro: false,
      street: location.street?.trim() ?? "",
      number: location.number?.trim() ?? "",
      city: location.city?.trim() ?? "",
      state: location.state?.trim() ?? "",
      ...(optionalText(location.complement)
        ? { complement: location.complement!.trim() }
        : {}),
      ...(optionalText(location.neighborhood)
        ? { neighborhood: location.neighborhood!.trim() }
        : {}),
      ...(optionalText(location.zipCode) ? { zipCode: location.zipCode!.trim() } : {}),
      ...(optionalText(location.reference)
        ? { reference: location.reference!.trim() }
        : {}),
      ...(optionalText(location.knownLocation)
        ? { knownLocation: location.knownLocation!.trim() }
        : {}),
    },
  };
};

const resolveAllDayTimes = (data: FormChatState) => {
  if (data.isAllDay !== true) {
    return {
      startTime: data.startTime ?? "",
      endTime: data.endTime ?? "",
    };
  }

  return {
    startTime: data.startTime || "00:00:00",
    endTime: data.endTime || "23:59:59",
  };
};

export const buildActivityFormChatPayload = ({
  data,
  terreiroId,
  mediumId,
  activityType,
}: BuildActivityPayloadInput): ActivityCreatePayload | Record<string, unknown> | null => {
  if (!terreiroId && !mediumId) {
    return null;
  }

  const memberIds = data.memberIds ?? [];
  const { startTime, endTime } = resolveAllDayTimes(data);

  const base: Record<string, unknown> = {
    title: data.title ?? "",
    description: data.description ?? "",
    isTemplate: data.isTemplate ?? false,
    isAllDay: data.isAllDay ?? false,
    minConfirmedMembers:
      memberIds.length === 0
        ? null
        : (data.minConfirmedMembers ??
          buildDefaultMinConfirmedMembers(memberIds.length)),
    members: memberIds,
    insumos: (data.insumos ?? []).map(({ id, quantity }) => ({
      id,
      quantity: Math.max(1, Math.floor(Number(quantity) || 1)),
    })),
  };

  const resolvedType =
    data.activityType === "RITUAL" || data.activityType === "ACTIVITY"
      ? data.activityType
      : activityType;
  if (resolvedType) {
    base.type = resolvedType;
  }

  if (terreiroId) {
    base.terreiroId = terreiroId;
    base.qrCheckinEnabled = data.qrCheckinEnabled !== false;
    base.qrReleaseOffsetMinutes = data.checkinStartWindowMinutes ?? 10;
    base.qrCheckinGraceMinutes = data.checkinEndWindowMinutes ?? 15;
    Object.assign(base, resolveActivityLocation(data));
  } else if (mediumId) {
    base.mediumId = mediumId;
  }

  if (data.isTemplate) {
    return omitFields(base, ["date", "startTime", "endTime"]);
  }

  return {
    ...base,
    date: data.date ?? "",
    startTime,
    endTime,
  };
};
